<div class="fixed inset-0 bg-gray-700 bg-opacity-75 flex justify-center items-center z-20">
    <div class="bg-white rounded p-4 min-w-md max-w-lg max-h-[90vh] w-full relative flex flex-col">

        <div class="flex flex-col mb-4 border-0 w-full shrink-0">
            <h1 class="font-semibold text-2xl text-primary-500">Edit Question</h1>
            <p class="text-primary-300 text-sm">Edit Question Details</p>
        </div>

        @let loading = (loading$ | async) ?? false;

        <form [formGroup]="mainForm" (ngSubmit)="onSubmit()" class="flex flex-col flex-1 overflow-hidden">

            <div class="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4">
                <div>
                    <label class="form-label">Question Text *</label>
                    <textarea formControlName="questionText" rows="2" required
                        class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500 focus:outline-none"></textarea>
                </div>

                <div>
                    <label class="form-label">Reference *</label>
                    <bible-reference-picker formControlName="reference"
                        [bibleEnum]="bibleBooks"></bible-reference-picker>
                    @if (mainForm.get('reference')?.touched && mainForm.get('reference')?.invalid) {
                    <div class="mt-1 flex items-center gap-1 text-xs text-red-600 tracking-wide">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20"
                            fill="currentColor">
                            <path fill-rule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                clip-rule="evenodd" />
                        </svg>
                        Reference incorrect
                    </div>
                    }
                </div>

                <div class="w-full flex flex-col mb-4">
                    <label class="form-label">
                        Explanation (Optional)
                    </label>
                    <textarea formControlName="explanation" rows="2"
                        class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500 focus:outline-none"></textarea>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="form-label">Difficulty *</label>
                        <input formControlName="difficulty" type="number" min="0" max="10"
                            class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500">
                    </div>
                    <div>
                        <label class="form-label">Score Points *</label>
                        <input formControlName="score" type="number" min="0" max="10"
                            class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500">
                    </div>
                </div>

                <div>
                    <label class="form-label">Question Type *</label>
                    <select formControlName="questionType"
                        class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500 text-primary-500">
                        @for (qType of questionTypesList; track qType.key) {
                        <option [value]="qType.key"
                            class="bg-white text-primary-500 text-sm">
                            {{ qType.key | questionTypeLabel }}
                        </option>
                        }
                    </select>
                </div>

                @if (mainForm.get('questionType')?.value === 'multipleChoice') {
                <div class="space-y-3">
                    <div class="flex justify-between items-center">
                        <label class="form-label !mb-0 text-secondary-600 font-bold">Answer Options *</label>
                        <button type="button" (click)="addOption()"
                            class="text-xs bg-secondary-50 text-secondary-600 px-2 py-1 rounded border border-secondary-200 hover:bg-secondary-100">
                            + Add Option
                        </button>
                    </div>

                    <div formArrayName="options" class="flex flex-col gap-2">
                        @for (optGroup of optionsArray.controls; track $index; let optIdx = $index) {
                        @let optId = optGroup.get('id')?.value;
                        @let isCorrect = mainForm.get('correctOptionId')?.value === optId;

                        <div [formGroupName]="optIdx"
                            class="flex items-center gap-3 p-2 rounded-md border transition-colors duration-200"
                            [class.bg-green-50]="isCorrect" [class.border-green-200]="isCorrect"
                            [class.bg-gray-50]="!isCorrect" [class.border-gray-100]="!isCorrect">

                            <input type="checkbox" [name]="'correct-' + optIdx" [checked]="isCorrect"
                                (change)="setCorrectOption(optId)"
                                class="h-4 w-4 rounded border-gray-300 text-green-500">

                            <input formControlName="text" placeholder="Option text..."
                                class="flex-1 bg-transparent border-none focus:ring-0 text-sm py-1"
                                [class.text-green-900]="isCorrect" [class.font-medium]="isCorrect">

                            <button type="button" (click)="removeOption(optIdx)"
                                class="transition-colors text-gray-400 hover:text-red-500">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        }
                    </div>
                    @if (mainForm.get('correctOptionId')?.invalid && mainForm.get('correctOptionId')?.touched) {
                    <p class="text-xs text-red-500">Please select a correct answer.</p>
                    }
                </div>
                } @else {
                <div class="space-y-1">
                    <label class="form-label text-secondary-600 font-bold">Correct Answer *</label>
                    @if (mainForm.get('questionType')?.value === 'trueFalse') {
                    <select formControlName="simpleAnswer"
                        class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500 text-primary-500">
                        <option [value]="'True'"
                            class="bg-white text-primary-500 text-sm">
                            True</option>
                        <option [value]="'False'"
                            class="bg-white text-primary-500 text-sm">
                            False</option>
                    </select>
                    } @else {
                    <input formControlName="simpleAnswer" placeholder="Enter the correct answer..."
                        class="w-full p-2 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-secondary-500 outline-none">
                    }
                </div>
                }
            </div>

            <div class="flex flex-row gap-4 justify-end pt-4 mt-2 border-t shrink-0">
                <button type="button" (click)="onCancel()" class="text-primary-500 px-4 py-2 focus:outline-none">
                    Cancel
                </button>
                <app-btn-submit [isDisabled]="mainForm.invalid || loading" [isLoading]="loading" actionText="Update">
                </app-btn-submit>
            </div>

        </form>
    </div>
</div>